import { Component, OnInit, ViewChild } from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { Router } from '@angular/router';
import { NgApexchartsModule } from 'ng-apexcharts';
import {
  ApexAxisChartSeries,
  ApexChart,
  ChartComponent,
  ApexDataLabels,
  ApexPlotOptions,
  ApexYAxis,
  ApexLegend,
  ApexStroke,
  ApexXAxis,
  ApexFill,
  ApexTooltip,
  ApexNonAxisChartSeries,
  ApexResponsive,
} from 'ng-apexcharts';
import { HomeService } from '../../../core/api/home.service';
import { LoadingService } from '../../../core/services/loading.service';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ProfileDialogComponent } from '../../../components/profile-dialog/profile-dialog.component';
import { AuthService } from '../../../core/services/auth.service';
import { FileService } from '../../../core/api/file.service';
import moment from 'moment';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  fill: ApexFill;
  tooltip: ApexTooltip;
  stroke: ApexStroke;
  legend: ApexLegend;
  colors: string[];
};

export type ChartOptions2 = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  responsive: ApexResponsive[];
  labels: any;
  colors: string[];
  plotOptions: ApexPlotOptions;
};

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    MatSelectModule,
    NgApexchartsModule,
    CommonModule,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit {
  @ViewChild('chart') chart?: ChartComponent;
  @ViewChild('donut') donut?: ChartComponent;
  years: any[] = [];
  selectedYear = new Date().getFullYear();
  reimburseData: any;
  cashAdvanceData: any;
  reimburseLogs: any[] = [];
  cashAdvanceLogs: any[] = [];
  currentUser: any;
  private avatarCache = new Map<number, string>();

  public chartOptions: ChartOptions = {
    series: [],
    chart: { type: 'bar', height: 320, width: '100%' as any },
    colors: ['#1F9AA2', '#91E4B7'],
    fill: { opacity: 1, colors: ['#1F9AA2', '#91E4B7'] },
    plotOptions: { bar: { horizontal: false, columnWidth: '55%' } },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2, colors: ['transparent'] },
    xaxis: { categories: [] },
    yaxis: { title: { text: 'Jumlah Transaksi' } },
    tooltip: { y: { formatter: (val) => val.toString() } },
    legend: { show: true },
  };

  public chartOptions2: ChartOptions2 = {
    series: [],
    chart: {
      type: 'donut',
      width: '100%',
      height: 320,
    },
    labels: ['Approved', 'On Going'],
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '18px',
              offsetY: -10,
            },
            value: {
              show: true,
              fontSize: '22px',
              fontWeight: 600,
              color: '#111',
            },
            total: {
              show: true,
              label: 'Total Transaction',
              fontSize: '18px',
              color: '#111',
              formatter: (w) => {
                // hitung total dari semua series
                return w.globals.seriesTotals
                  .reduce((a: any, b: any) => a + b, 0)
                  .toString();
              },
            },
          },
        },
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: '100%',
            height: 240,
          },
          legend: {
            position: 'bottom',
          },
        },
      },
    ],
    colors: ['#5582C9', '#AACAFD'],
  };

  constructor(
    private router: Router,
    private service: HomeService,
    private loadingService: LoadingService,
    private toastrService: ToastrService,
    private dialog: MatDialog,
    public authService: AuthService,
    private fileService: FileService,
  ) {}

  ngOnInit(): void {
    this.currentUser = this.authService.getLoggedUser();
    this.getStatistic();
    this.getLog();
    const currentYear = new Date().getFullYear();
    const startYear = 1900; // bisa kamu sesuaikan
    for (let y = currentYear; y >= startYear; y--) {
      this.years.push(y);
    }
  }

  getStatistic(value?: any): void {
    const year = value?.value ?? this.selectedYear;
    this.selectedYear = year;
    this.loadingService.startLoading();
    this.service
      .getStatistic({ year })
      .then((res) => {
        this.loadingService.endLoading();
        const data = res?.monthlyTransaction ?? [];

        if (Array.isArray(data)) {
          const categories = data.map((item: any) => item.month);
          const reimburseData = data.map(
            (item: any) => item.reimburse_transaction
          );
          const cashAdvanceData = data.map(
            (item: any) => item.cashadvance_transaction
          );

          this.chartOptions = {
            ...this.chartOptions,
            series: [
              { name: 'Reimbursement', data: reimburseData } as any,
              { name: 'Cash Advance', data: cashAdvanceData } as any,
            ],
            xaxis: { categories },
          };

          try {
            this.chart?.updateOptions?.({ xaxis: { categories } }, false, true);
            this.chart?.updateSeries?.(this.chartOptions.series, true);
          } catch {
            // ignore chart update errors
          }
        }

        const status = res?.statusSubmission ?? {
          ongoing: 0,
          done: 0,
          total_submission: 0,
        };

        this.chartOptions2 = {
          ...this.chartOptions2,
          series: [status.done, status.ongoing],
          labels: ['Approved', 'On Going'],
        };

        try {
          this.donut?.updateSeries?.(this.chartOptions2.series, true);
        } catch {
          // ignore chart update errors
        }
      })
      .catch((error) => {
        this.toastrService.error(error.message);
        this.loadingService.endLoading();
      });
  }

  getLog(): void {
    this.loadingService.startLoading();
    this.service
      .getLog()
      .then((res) => {
        this.loadingService.endLoading();
        this.reimburseLogs = (res.reimburseLog ?? []).map((item: any) => ({
          ...item,
          updated_at: this.toLogDate(item.updated_at),
          created_at: this.toLogDate(item.created_at),
        }));
        this.cashAdvanceLogs = (res.cashAdvanceLog ?? []).map((item: any) => ({
          ...item,
          updated_at: this.toLogDate(item.updated_at),
          created_at: this.toLogDate(item.created_at),
        }));
        this.reimburseData = res.reimburseData;
        this.cashAdvanceData = res.cashAdvanceData;
        this.reimburseLogs.forEach((item: any) => {
          this.getImageUrl(item.updater);
        });
        this.cashAdvanceLogs.forEach((item: any) => {
          this.getImageUrl(item.creator);
        });
      })
      .catch((error) => {
        this.toastrService.error(error.message);
        this.loadingService.endLoading();
      });
  }

  createReimbursement() {
    this.router.navigate(['/dashboard/reimbursement-claimer/action'], {
      queryParams: { m: 'add' },
    });
  }

  createCashAdvance() {
    this.router.navigate(['/dashboard/cash-advance-claimer/action'], {
      queryParams: { m: 'add' },
    });
  }

  openProfile(userId: any) {
    this.dialog
      .open(ProfileDialogComponent, {
        maxWidth: '70vw',
        maxHeight: '95vh',
        data: userId,
      })
      .afterClosed()
      .subscribe((item) => {});
  }

  detailReimburse(id: any) {
    this.router.navigate(['/dashboard/reimbursement-approval/action'], {
      queryParams: {
        m: 'detail',
        id,
      },
    });
  }

  detailCashAdvance(id: any) {
    this.router.navigate(['/dashboard/cash-advance-approval/action'], {
      queryParams: {
        m: 'detail',
        id,
      },
    });
  }

  getReimburseLogAccentClass(item: any): string {
    if (this.isLogRejected(item)) return 'timeline-accent--rejected';
    if (item.created_by == this.reimburseData?.created_by) {
      return 'timeline-accent--creator';
    }
    if (this.authService.isFinance(item?.updater)) {
      return 'timeline-accent--finance';
    }
    return 'timeline-accent--approver';
  }

  getCashAdvanceLogAccentClass(item: any): string {
    if (this.isLogRejected(item)) return 'timeline-accent--rejected';
    if (item.created_by == this.cashAdvanceData?.created_by) {
      return 'timeline-accent--creator';
    }
    if (item.creator?.role?.code == 'FIN') return 'timeline-accent--finance';
    return 'timeline-accent--approver';
  }

  isLogRejected(item: any): boolean {
    return !!item.description?.includes('Rejected');
  }

  isWaitingLog(item: any): boolean {
    return !!item.description?.includes('Waiting');
  }

  getLogTimestamp(item: any): Date | null {
    return this.toLogDate(item.updated_at) ?? this.toLogDate(item.created_at);
  }

  private toLogDate(value: any): Date | null {
    if (!value) return null;
    const parsed = moment(value);
    return parsed.isValid() ? parsed.toDate() : null;
  }

  private getImageUrl(user: any) {
    if (!user?.file?.id) {
      user.imageUrl = null;
      return;
    }

    if (user.id && this.avatarCache.has(user.id)) {
      user.imageUrl = this.avatarCache.get(user.id);
      return;
    }

    this.downloadAvatar(user, user.file.id);
  }

  private downloadAvatar(user: any, fileId: number) {
    this.fileService.download(fileId).subscribe(
      (res) => {
        const url = URL.createObjectURL(res);
        user.imageUrl = url;
        if (user.id) {
          this.avatarCache.set(user.id, url);
        }
      },
      () => {
        user.imageUrl = null;
      }
    );
  }
}
